// react
import React, { useState } from "react";
// ui components
import {
  Button,
  Dialog,
  Drawer,
  Heading,
  OTPInput,
  PageHeader,
  TextArea,
  TextField,
} from "../UI";
// custom state
import { useContactUs } from "./useContactUs";
// constants (icon)
import {
  CallIcon,
  GetDetailsIcon,
  LocationIcon,
  MailIcon,
} from "../../constants/icons";
// css
import "./contact-us.css";
import { useAppDispatch } from "../../../hooks/useAppDispatch";
import { hideLoader, showLoader } from "../../../actions/UserActions";
import { showHttpError } from "../../../utils/message";
import { generateRegistrationOTP } from "../../../services/otp";
import { OtpTypes } from "../../../utils/otp";
import { toastMessage } from "../../../Components/showMessages/toastMessage";
import { useWidth } from "../../hooks/useMobile";
import { SubmitButton } from "../../../Components/Button/AllButton";
import { generateContactUsLead } from "../../../services/contact-us";

// variables
const linkIcons = [<LocationIcon />, <MailIcon />, <CallIcon />];

const MESSAGE_MIN_LENGTH = 50;

// FC: Contact Us Component
const ContactUs = () => {
  // State
  const {
    firstName,
    setFirstName,
    lastName,
    setLastName,
    contact,
    setContact,
    message,
    setMessage,
    subject,
    setSubject,
    otp,
    setOtp,
  } = useContactUs();
  const [isOtpModalOpen, setIsOtpModalOpen] = useState(false);
  const dispatch = useAppDispatch();
  const { mobileView } = useWidth();

  const canSendOtp =
    firstName &&
    lastName &&
    contact &&
    String(contact).length === 10 &&
    subject &&
    message.length > 2;

  async function handleSendOtp() {
    if(message.length < MESSAGE_MIN_LENGTH) {
      return showHttpError(`Message should be at least ${MESSAGE_MIN_LENGTH} characters long`);
    }
    try {
      await generateRegistrationOTP({
        type: OtpTypes.ContactUsVerification,
        userContact: Number(contact),
      });
      dispatch(showLoader());
      setIsOtpModalOpen(true);
      toastMessage("OTP sent successfully!");
    } catch (error: any) {
      showHttpError(error?.message);
    } finally {
      dispatch(hideLoader());
    }
  }

  async function handleOtpSubmitAndContactUsLead() {
    try {
      dispatch(showLoader());
      const payload = {
        name: {
          fName: firstName,
          lName: lastName,
        },
        contact: Number(contact),
        message,
        subject,
        otp: Number(otp),
      };
      await generateContactUsLead(payload);
      toastMessage("We'll get back to you!");
      resetForm();
    } catch (error: any) {
      showHttpError(error?.message);
    } finally {
      dispatch(hideLoader());
      setIsOtpModalOpen(false);
    }
  }

  function resetForm() {
    setFirstName("");
    setLastName("");
    setContact("");
    setMessage("");
    setSubject("");
    setOtp("");
  }

  // component return
  return (
    <main className="contact-us-container contact-us | bg-white-800">
      <section className="page-container-grid page-container-auto-grid">
        {/* Contact Us Header */}
        <PageHeader title="Contact Us" imgSrc="contact-us/bg.webp" />
        <div className="flex-center py-2">
          {/* Contact Us Form */}
          <div
            className="| small-container flex-col gap-1 br-1 bg-white-900 p-3 m-p-1"
          >
            <h1 className="| m-0 text-center mx-auto text-primary-400 fs-subheading fw-600">
              Having Any Query! Or Book an appointment
            </h1>
            {/* Name */}
            <div className="two-column m-one-column gap-1">
              <TextField
                label="First Name"
                placeholder="Enter First Name"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
              />
              <TextField
                label="Last Name"
                placeholder="Enter Last Name"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
              />
            </div>
            <TextField
              type="tel"
              maxLength={10}
              label="Contact Number"
              placeholder="Enter Contact Number"
              value={contact}
              onChange={(e) => setContact(e.target.value)}
            />
            <TextField
              label="Subject"
              placeholder="Enter Subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
            />
            {/* Message */}
            <TextArea
              label="Your Message"
              placeholder="Enter your Message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
            />
            {/* Submit Button */}
            <Button
              variant="primary"
              action="secondary"
              Icon={<GetDetailsIcon />}
              className="| fs-button as-start"
              onClick={handleSendOtp}
              disabled={!canSendOtp}
            >
              Submit
            </Button>
          </div>
        </div>
      </section>

      {mobileView ? (
        <Drawer
          state={isOtpModalOpen}
          setState={setIsOtpModalOpen}
          drawerHeading="Enter OTP"
          position="bottom"
          maxHeight="full"
          drawerBody={
            <div className="flex-col gap-1">
              <OTPInput
                label="Enter OTP"
                onOtpComplete={(value) => setOtp(value)}
              />
            </div>
          }
          drawerFooter={
            <SubmitButton
              type="button"
              onClick={handleOtpSubmitAndContactUsLead}
              fullWidth
            />
          }
        />
      ) : (
        <Dialog
          state={isOtpModalOpen}
          setState={setIsOtpModalOpen}
          header="Enter OTP"
          maxWidth="sm"
          body={
            <div className="flex-col gap-1">
              <OTPInput
                label="Enter OTP"
                onOtpComplete={(value) => setOtp(value)}
              />
            </div>
          }
          footer={
            <SubmitButton
              type="button"
              onClick={handleOtpSubmitAndContactUsLead}
              action="secondary"
            />
          }
        ></Dialog>
      )}

      {/* Contact Us Information */}
      <section className="| flex-col gap-20 bg-primary-400 p-3 text-white-900">
        {/* Contact Us Header */}
        <Heading className="text-white-900">Contact Info</Heading>
        {/* Contact Us Information List */}
        <ul className="| three-column t-one-column m-one-column gap-1 contact-info-list">
          {/* Location */}
          <li className="| flex-col-center gap-1 p-1 br-4 bg-primary-500">
            <span className="| fs-icon">{linkIcons[0]}</span>
            <p className="| m-0 fs-link text-center fw-400">
              02/13 Vallabh Flats, Vijay Chowk, NR Sardar Patel Diamond Market,
              Bapunagar, Ahmedabad, Gujarat 380024
            </p>
          </li>
          {/* Mail */}
          <li className="| flex-col-center gap-1 p-1 br-4 bg-primary-500">
            <span className="| fs-icon">{linkIcons[1]}</span>
            <a
              href="mailto:customersupport@kabraexpress.com"
              className="| m-0 fs-link text-center text-white-900 fw-400"
            >
              customersupport@kabraexpress.com
            </a>
          </li>
          {/* Contact Number */}
          <li className="| flex-col-center gap-1 p-1 br-4 bg-primary-500">
            <span className="| fs-icon">{linkIcons[2]}</span>
            <div className="| flex-col gap-4">
              <a
                href="tel:7575857777"
                className="m-0 fs-medium text-center text-white-900 fw-400"
              >
                +917575857777
              </a>
              <a
                href="tel:6358880246"
                className="m-0 fs-medium text-center text-white-900 fw-400"
              >
                +916358880246
              </a>
            </div>
          </li>
        </ul>
      </section>
    </main>
  );
};

export default ContactUs;
